import React, { useEffect, useId, useRef, useState } from "react";
import { IconChevronDown } from "./icons";
import { CONTROL, LABEL } from "./controlStyles";

/**
 * Button + panel with a checkbox list. options: [{ value, label }], selected: [value, ...].
 * An empty selection means "Svi" (all). Closes on a click outside the panel and on Escape.
 * With more than 5 options, a search field appears in the panel.
 */
export default function MultiSelectDropdown({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const labelId = useId();

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch("");
      // slightly delayed so the panel gets drawn before focusing
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  const toggleValue = (value) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const filteredOptions = search
    ? options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  let summary = "Svi";
  if (selected.length === 1) summary = options.find((o) => o.value === selected[0])?.label ?? "1 izabrano";
  else if (selected.length > 1) summary = `${selected.length} izabrano`;

  return (
    <div
      className="relative"
      ref={rootRef}
      onKeyDown={(e) => e.key === "Escape" && open && setOpen(false)}
    >
      <div className="flex flex-col gap-1.5">
        <span id={labelId} className={LABEL}>
          {label}
        </span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={open}
          aria-labelledby={labelId}
          className={`${CONTROL} flex min-w-[10rem] max-w-[16rem] items-center justify-between gap-2 text-left font-medium`}
        >
          <span className="truncate">{summary}</span>
          <IconChevronDown
            aria-hidden="true"
            className={`shrink-0 text-sm text-slate-400 transition-transform duration-150 dark:text-slate-500 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <div
          className="absolute z-dropdown mt-1 w-64 max-w-[calc(100vw-2rem)] animate-pop-in rounded-lg border border-slate-200 bg-surface p-2 shadow-pop
            dark:border-slate-700 dark:bg-slate-900 dark:shadow-pop-dark"
        >
          {options.length > 5 && (
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži"
              aria-label={`Pretraži: ${label}`}
              className={`${CONTROL} mb-2 h-8 w-full`}
            />
          )}
          <div className="scroll-thin max-h-56 overflow-y-auto" role="group" aria-label={label}>
            {filteredOptions.length === 0 && (
              <p className="px-2 py-1.5 text-sm text-slate-500 dark:text-slate-400">
                {options.length === 0 ? "Nema opcija" : "Nema poklapanja"}
              </p>
            )}
            {filteredOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => toggleValue(opt.value)}
                  className="h-4 w-4 rounded accent-primary"
                />
                <span className="min-w-0 break-words">{opt.label}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-2 w-full rounded-md border-t border-slate-100 px-2 pt-2 text-left text-xs font-medium text-primary hover:underline dark:border-slate-800 dark:text-primary-light"
            >
              Poništi izbor (prikaži sve)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
